import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { baseUrl } from "../index";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { PfInput } from "./ProposalFormFields";
import "./MemberQuestionsForm.css";
import { updateTravellerDetails } from "../redux/slices/TravellerDetailsSlice";
import API from "../utils/API";

const NON_RESIDENT_MSG =
    "Policy cannot be issued as the proposer is a non-resident Indian.";
const IMMIGRATION_VISA_MSG =
    "Policy cannot be issued as the traveler travelling on Immigration Visa.";
const PRE_EXISTING_MSG =
    "Apologies, but we cannot provide coverage for under this policy. Kindly call our toll-free number 1800 2666 3666 for more details.";

const QUESTIONS = [
  {
    key: "isIndianCitizen",
    question:
      "Is the traveler an Indian citizen and currently in India while taking the policy?",
  },
  {
    key: "isImmigrationVisa",
    question: "Is the traveler traveling on an immigration visa?",
  },
  {
    key: "hasPreExistingCondition",
    question:
      "Does any of the travelers possess pre-existing medical conditions?",
  },
];

// FIND the entire doctorDetailsSchema and companyDetailsSchema and replace both:

const NAME_MIN_TWO_WORDS = (label) =>
  Yup.string()
    .trim()
    .required(`${label} is required`)
    .test(
      "two-words",
      `${label} must contain at least a first and last name`,
      (val) => {
        if (!val) return false;
        const parts = val.trim().split(/\s+/);
        return parts.length >= 2 && parts.every((p) => p.length >= 1);
      },
    )
    .max(100, `${label} must be at most 100 characters`);

const MOBILE_SCHEMA = Yup.string()
  .required("Mobile number is required")
  .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number")
  .test(
    "no-repeated-digits",
    "Mobile number cannot have the same digit repeated more than 5 times",
    (val) => {
      if (!val) return true;
      return !/(\d)\1{5,}/.test(val);
    },
  );

const ADDRESS_LINE = (label) =>
  Yup.string()
    .trim()
    .required(`${label} is required`)
    .max(100, `${label} must be at most 100 characters`);

const PINCODE_SCHEMA = Yup.string()
  .matches(/^\d{6}$/, "Enter a valid 6-digit pincode")
  .required("Pincode is required");

const doctorDetailsSchema = Yup.object({
  enabled: Yup.boolean().required(),
  doctorName: Yup.string().when("enabled", {
    is: true,
    then: () => NAME_MIN_TWO_WORDS("Doctor name"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine1: Yup.string().when("enabled", {
    is: true,
    then: () => ADDRESS_LINE("Address line 1"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine2: Yup.string().when("enabled", {
    is: true,
    then: () => ADDRESS_LINE("Address line 2"),
    otherwise: (schema) => schema.notRequired(),
  }),
  mobileNo: Yup.string().when("enabled", {
    is: true,
    then: () => MOBILE_SCHEMA,
    otherwise: (schema) => schema.notRequired(),
  }),
  pincode: Yup.string().when("enabled", {
    is: true,
    then: () => PINCODE_SCHEMA,
    otherwise: (schema) => schema.notRequired(),
  }),
});

const sponsorDetailsSchema = Yup.object({
  enabled: Yup.boolean().required(),
  sponsorName: Yup.string().when("enabled", {
    is: true,
    then: () => NAME_MIN_TWO_WORDS("Sponsor name"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine1: Yup.string().when("enabled", {
    is: true,
    then: () => ADDRESS_LINE("Address line 1"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine2: Yup.string().when("enabled", {
    is: true,
    then: () => ADDRESS_LINE("Address line 2"),
    otherwise: (schema) => schema.notRequired(),
  }),
  mobileNo: Yup.string().when("enabled", {
    is: true,
    then: () => MOBILE_SCHEMA,
    otherwise: (schema) => schema.notRequired(),
  }),
  pincode: Yup.string().when("enabled", {
    is: true,
    then: () => PINCODE_SCHEMA,
    otherwise: (schema) => schema.notRequired(),
  }),
});

const companyDetailsSchema = Yup.object({
  enabled: Yup.boolean().required(),
  companyName: Yup.string().when("enabled", {
    is: true,
    then: (schema) =>
      schema
        .trim()
        .required("Company name is required")
        .max(100, "Company name must be at most 100 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine1: Yup.string().when("enabled", {
    is: true,
    then: () => ADDRESS_LINE("Address line 1"),
    otherwise: (schema) => schema.notRequired(),
  }),
  addressLine2: Yup.string().when("enabled", {
    is: true,
    then: () => ADDRESS_LINE("Address line 2"),
    otherwise: (schema) => schema.notRequired(),
  }),
  mobileNo: Yup.string().when("enabled", {
    is: true,
    then: () => MOBILE_SCHEMA,
    otherwise: (schema) => schema.notRequired(),
  }),
  pincode: Yup.string().when("enabled", {
    is: true,
    then: () => PINCODE_SCHEMA,
    otherwise: (schema) => schema.notRequired(),
  }),
});

const buildSchema = ({ isStudentTrip, isAnnualMultiTrip }) =>
  Yup.object({
    /*answers: Yup.object({
      isIndianCitizen: Yup.boolean().required("Please select Yes or No"),
      isImmigrationVisa: Yup.boolean().required("Please select Yes or No"),
      hasPreExistingCondition: Yup.boolean().required(
        "Please select Yes or No",
      ),
    }),*/

      answers: Yup.object({
          isIndianCitizen: Yup.boolean()
              .nullable()
              .required("Please select Yes or No")
              .test("must-be-resident", NON_RESIDENT_MSG, (val) => val !== false),
          isImmigrationVisa: Yup.boolean()
              .nullable()
              .required("Please select Yes or No")
              .test("no-immigration-visa", IMMIGRATION_VISA_MSG, (val) => val !== true),
          hasPreExistingCondition: Yup.boolean()
              .nullable()
              .required("Please select Yes or No")
              .test("no-pre-existing", PRE_EXISTING_MSG, (val) => val !== true),
      }),

    doctorDetails: doctorDetailsSchema,
    sponsorDetails: sponsorDetailsSchema,
    studentDetails: Yup.object({
      universityName: isStudentTrip
        ? Yup.string()
            .trim()
            .required("University name is required")
            .max(120, "University name must be at most 120 characters")
        : Yup.string().notRequired(),
      courseDuration: isStudentTrip
        ? Yup.string()
            .trim()
            .required("Course duration is required")
            .max(60, "Course duration must be at most 60 characters")
        : Yup.string().notRequired(),
      numberOfSemester: isStudentTrip
        ? Yup.string()
            .matches(/^\d+$/, "Number of semester must be a valid number")
            .required("Number of semester is required")
        : Yup.string().notRequired(),
    }),
    companyDetails: Yup.object({
      enabled: Yup.boolean().required(),
      companyName: isAnnualMultiTrip
        ? Yup.string().when("enabled", {
            is: true,
            then: (schema) =>
              schema
                .trim()
                .required("Company name is required")
                .max(100, "Company name must be at most 100 characters"),
            otherwise: (schema) => schema.notRequired(),
          })
        : Yup.string().notRequired(),
      addressLine1: isAnnualMultiTrip
        ? Yup.string().when("enabled", {
            is: true,
            then: () => ADDRESS_LINE("Address line 1"),
            otherwise: (schema) => schema.notRequired(),
          })
        : Yup.string().notRequired(),
      addressLine2: isAnnualMultiTrip
        ? Yup.string().when("enabled", {
            is: true,
            then: () => ADDRESS_LINE("Address line 2"),
            otherwise: (schema) => schema.notRequired(),
          })
        : Yup.string().notRequired(),
      mobileNo: isAnnualMultiTrip
        ? Yup.string().when("enabled", {
            is: true,
            then: () => MOBILE_SCHEMA,
            otherwise: (schema) => schema.notRequired(),
          })
        : Yup.string().notRequired(),
      pincode: isAnnualMultiTrip
        ? Yup.string().when("enabled", {
            is: true,
            then: () => PINCODE_SCHEMA,
            otherwise: (schema) => schema.notRequired(),
          })
        : Yup.string().notRequired(),
    }),
  });

const initialValues = {
  answers: {
    isIndianCitizen: null,
    isImmigrationVisa: null,
    hasPreExistingCondition: null,
  },
  doctorDetails: {
    enabled: false,
    doctorName: "",
    addressLine1: "",
    addressLine2: "",
    mobileNo: "",
    pincode: "",
  },
  sponsorDetails: {
    enabled: false,
    sponsorName: "",
    addressLine1: "",
    addressLine2: "",
    mobileNo: "",
    pincode: "",
  },
  studentDetails: {
    universityName: "",
    courseDuration: "",
    numberOfSemester: "",
  },
  companyDetails: {
    enabled: false,
    companyName: "",
    addressLine1: "",
    addressLine2: "",
    mobileNo: "",
    pincode: "",
  },
};

const SyncAnswers = ({ onChange, onValidityChange }) => {
  const { values, isValid } = useFormikContext();

  useEffect(() => {
    onChange?.(values.answers);
  }, [values.answers, onChange]);

  useEffect(() => {
    onValidityChange?.(Boolean(isValid));
  }, [isValid, onValidityChange]);

  return null;
};

const MemberQuestionsForm = ({ onChange, onValidityChange }) => {
  const dispatch = useDispatch();
  const travellerDetails = useSelector((state) => state.TravellerDetails);
  const EnquiryDetails = useSelector((state) => state.Enquiry);
  console.log(EnquiryDetails);
  // ADD this block right before:  return (
  //   <Formik ...
  const savedCompanyDetails =
    travellerDetails?.companyDetails || initialValues.companyDetails;
  const savedDoctorDetails =
    travellerDetails?.doctorDetails || initialValues.doctorDetails;
  const savedSponsorDetails =
    travellerDetails?.sponsorDetails || initialValues.sponsorDetails;
  const savedStudentDetails =
    travellerDetails?.studentDetails || initialValues.studentDetails;
  const [doctorPincodeCity, setDoctorPincodeCity] = useState(
    savedDoctorDetails?.pincodeCity || null,
  );
  const [sponsorPincodeCity, setSponsorPincodeCity] = useState(
    savedSponsorDetails?.pincodeCity || null,
  );
  const [companyPincodeCity, setCompanyPincodeCity] = useState(
    savedCompanyDetails?.pincodeCity || null,
  );
  const [doctorPincodeLoading, setDoctorPincodeLoading] = useState(false);
  const [sponsorPincodeLoading, setSponsorPincodeLoading] = useState(false);
  const [companyPincodeLoading, setCompanyPincodeLoading] = useState(false);
  const isStudentTrip = String(EnquiryDetails?.EnquiryData?.tripType || "")
    .trim()
    .toLowerCase()
    .includes("student");

  const fetchPincodeCity = useCallback(
    async (pincode, companyId, onSuccess, setLoading) => {
      if (!/^\d{6}$/.test(pincode)) return;
      setLoading(true);
      const body = {
        companyId: String(companyId),
        Pincode: pincode,
      };
      try {
        const response = await API.CallRequestWithHeader({
          URL: "GetCityStateByPincode",
          PostString: JSON.stringify(body),
        });
        if (response?.StatusCode === 200) {
          const data = response?.Response;
          const state = Array.isArray(data) ? data[0] || null : data || null;
          console.log(state);

          onSuccess(state);
        }
      } catch (e) {
        console.error("Pincode fetch failed", e);
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  const tripType = String(EnquiryDetails?.EnquiryData?.tripType || "")
    .trim()
    .toLowerCase();
  console.log(tripType);

  const isAnnualMultiTrip = tripType.includes("annual multi trip");
  const validationSchema = useMemo(
    () =>
      buildSchema({
        isStudentTrip,
        isAnnualMultiTrip,
      }),
    [isAnnualMultiTrip, isStudentTrip],
  );

  const savedAnswers =
    travellerDetails?.memberQuestions ||
    travellerDetails?.questions ||
    travellerDetails?.medicalQuestions ||
    initialValues.answers;

  const hydratedValues = useMemo(
    () => ({
      answers: {
        isIndianCitizen:
          savedAnswers?.isIndianCitizen ??
          initialValues.answers.isIndianCitizen,
        isImmigrationVisa:
          savedAnswers?.isImmigrationVisa ??
          initialValues.answers.isImmigrationVisa,
        hasPreExistingCondition:
          savedAnswers?.hasPreExistingCondition ??
          initialValues.answers.hasPreExistingCondition,
      },
      doctorDetails: {
        enabled: Boolean(savedDoctorDetails?.enabled),
        doctorName: savedDoctorDetails?.doctorName || "",
        addressLine1: savedDoctorDetails?.addressLine1 || "",
        addressLine2: savedDoctorDetails?.addressLine2 || "",
        mobileNo: savedDoctorDetails?.mobileNo || "",
        pincode: savedDoctorDetails?.pincode || "",
      },
      sponsorDetails: {
        enabled: Boolean(savedSponsorDetails?.enabled),
        sponsorName: savedSponsorDetails?.sponsorName || "",
        addressLine1: savedSponsorDetails?.addressLine1 || "",
        addressLine2: savedSponsorDetails?.addressLine2 || "",
        mobileNo: savedSponsorDetails?.mobileNo || "",
        pincode: savedSponsorDetails?.pincode || "",
      },
      studentDetails: {
        universityName: savedStudentDetails?.universityName || "",
        courseDuration: savedStudentDetails?.courseDuration || "",
        numberOfSemester: savedStudentDetails?.numberOfSemester || "",
      },
      companyDetails: {
        enabled: Boolean(savedCompanyDetails?.enabled),
        companyName: savedCompanyDetails?.companyName || "",
        addressLine1: savedCompanyDetails?.addressLine1 || "",
        addressLine2: savedCompanyDetails?.addressLine2 || "",
        mobileNo: savedCompanyDetails?.mobileNo || "",
        pincode: savedCompanyDetails?.pincode || "",
      },
    }),
    [
      savedAnswers,
      savedDoctorDetails,
      savedSponsorDetails,
      savedStudentDetails,
      savedCompanyDetails,
    ],
  );

  return (
    <Formik
      initialValues={hydratedValues}
      validationSchema={validationSchema}
      validateOnMount
      validateOnChange
      validateOnBlur
      enableReinitialize
      onSubmit={() => {}}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
        const show = (field) =>
          Boolean(touched.answers?.[field] && errors.answers?.[field]);
        const showDoctor = (field) =>
          Boolean(
            values.doctorDetails?.enabled &&
            touched.doctorDetails?.[field] &&
            errors.doctorDetails?.[field],
          );
        const showSponsor = (field) =>
          Boolean(
            values.sponsorDetails?.enabled &&
            touched.sponsorDetails?.[field] &&
            errors.sponsorDetails?.[field],
          );
        const showStudent = (field) =>
          Boolean(
            touched.studentDetails?.[field] && errors.studentDetails?.[field],
          );
        const showCompany = (field) =>
          Boolean(
            values.companyDetails?.enabled &&
            touched.companyDetails?.[field] &&
            errors.companyDetails?.[field],
          );

        const setAnswer = (field, value) => {
          setFieldValue(`answers.${field}`, value, true);
          setFieldTouched(`answers.${field}`, true, false);
          const nextAnswers = {
            ...values.answers,
            [field]: value,
          };
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: nextAnswers,
              questions: nextAnswers,
              medicalQuestions: nextAnswers,
              doctorDetails: values.doctorDetails,
              sponsorDetails: values.sponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: values.companyDetails,
            }),
          );
        };

        const setDoctorField = (field, value) => {
          setFieldValue(`doctorDetails.${field}`, value, true);
          setFieldTouched(`doctorDetails.${field}`, true, false);
          const nextDoctorDetails = {
            ...values.doctorDetails,
            [field]: value,
          };
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: nextDoctorDetails,
              sponsorDetails: values.sponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: values.companyDetails,
            }),
          );
        };

        const toggleDoctorDetails = (checked) => {
          setFieldValue("doctorDetails.enabled", checked, true);
          setFieldTouched("doctorDetails.enabled", true, false);
          const nextDoctorDetails = {
            ...values.doctorDetails,
            enabled: checked,
            doctorName: checked ? values.doctorDetails?.doctorName || "" : "",
            addressLine1: checked
              ? values.doctorDetails?.addressLine1 || ""
              : "",
            addressLine2: checked
              ? values.doctorDetails?.addressLine2 || ""
              : "",
            mobileNo: checked ? values.doctorDetails?.mobileNo || "" : "",
            pincode: checked ? values.doctorDetails?.pincode || "" : "",
          };
          setFieldValue(
            "doctorDetails.doctorName",
            nextDoctorDetails.doctorName,
            false,
          );
          setFieldValue(
            "doctorDetails.addressLine1",
            nextDoctorDetails.addressLine1,
            false,
          );
          setFieldValue(
            "doctorDetails.addressLine2",
            nextDoctorDetails.addressLine2,
            false,
          );
          setFieldValue(
            "doctorDetails.mobileNo",
            nextDoctorDetails.mobileNo,
            false,
          );
          setFieldValue(
            "doctorDetails.pincode",
            nextDoctorDetails.pincode,
            false,
          );
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: nextDoctorDetails,
              sponsorDetails: values.sponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: values.companyDetails,
            }),
          );
        };

        const setSponsorField = (field, value) => {
          setFieldValue(`sponsorDetails.${field}`, value, true);
          setFieldTouched(`sponsorDetails.${field}`, true, false);
          const nextSponsorDetails = {
            ...values.sponsorDetails,
            [field]: value,
          };
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: values.doctorDetails,
              sponsorDetails: nextSponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: values.companyDetails,
            }),
          );
        };

        const toggleSponsorDetails = (checked) => {
          setFieldValue("sponsorDetails.enabled", checked, true);
          setFieldTouched("sponsorDetails.enabled", true, false);
          const nextSponsorDetails = {
            ...values.sponsorDetails,
            enabled: checked,
            sponsorName: checked
              ? values.sponsorDetails?.sponsorName || ""
              : "",
            addressLine1: checked
              ? values.sponsorDetails?.addressLine1 || ""
              : "",
            addressLine2: checked
              ? values.sponsorDetails?.addressLine2 || ""
              : "",
            mobileNo: checked ? values.sponsorDetails?.mobileNo || "" : "",
            pincode: checked ? values.sponsorDetails?.pincode || "" : "",
          };
          setFieldValue(
            "sponsorDetails.sponsorName",
            nextSponsorDetails.sponsorName,
            false,
          );
          setFieldValue(
            "sponsorDetails.addressLine1",
            nextSponsorDetails.addressLine1,
            false,
          );
          setFieldValue(
            "sponsorDetails.addressLine2",
            nextSponsorDetails.addressLine2,
            false,
          );
          setFieldValue(
            "sponsorDetails.mobileNo",
            nextSponsorDetails.mobileNo,
            false,
          );
          setFieldValue(
            "sponsorDetails.pincode",
            nextSponsorDetails.pincode,
            false,
          );
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: values.doctorDetails,
              sponsorDetails: nextSponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: values.companyDetails,
            }),
          );
        };

        const setStudentField = (field, value) => {
          setFieldValue(`studentDetails.${field}`, value, true);
          setFieldTouched(`studentDetails.${field}`, true, false);
          const nextStudentDetails = {
            ...values.studentDetails,
            [field]: value,
          };
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: values.doctorDetails,
              sponsorDetails: values.sponsorDetails,
              studentDetails: nextStudentDetails,
              companyDetails: values.companyDetails,
            }),
          );
        };

        const setCompanyField = (field, value) => {
          setFieldValue(`companyDetails.${field}`, value, true);
          setFieldTouched(`companyDetails.${field}`, true, false);
          const nextCompanyDetails = {
            ...values.companyDetails,
            [field]: value,
          };
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: values.doctorDetails,
              sponsorDetails: values.sponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: nextCompanyDetails,
            }),
          );
        };

        const toggleCompanyDetails = (checked) => {
          setFieldValue("companyDetails.enabled", checked, true);
          setFieldTouched("companyDetails.enabled", true, false);
          const nextCompanyDetails = {
            ...values.companyDetails,
            enabled: checked,
            companyName: checked
              ? values.companyDetails?.companyName || ""
              : "",
            addressLine1: checked
              ? values.companyDetails?.addressLine1 || ""
              : "",
            addressLine2: checked
              ? values.companyDetails?.addressLine2 || ""
              : "",
            mobileNo: checked ? values.companyDetails?.mobileNo || "" : "",
            pincode: checked ? values.companyDetails?.pincode || "" : "",
          };
          setFieldValue(
            "companyDetails.companyName",
            nextCompanyDetails.companyName,
            false,
          );
          setFieldValue(
            "companyDetails.addressLine1",
            nextCompanyDetails.addressLine1,
            false,
          );
          setFieldValue(
            "companyDetails.addressLine2",
            nextCompanyDetails.addressLine2,
            false,
          );
          setFieldValue(
            "companyDetails.mobileNo",
            nextCompanyDetails.mobileNo,
            false,
          );
          setFieldValue(
            "companyDetails.pincode",
            nextCompanyDetails.pincode,
            false,
          );
          dispatch(
            updateTravellerDetails({
              ...travellerDetails,
              memberQuestions: values.answers,
              questions: values.answers,
              medicalQuestions: values.answers,
              doctorDetails: values.doctorDetails,
              sponsorDetails: values.sponsorDetails,
              studentDetails: values.studentDetails,
              companyDetails: nextCompanyDetails,
            }),
          );
        };

        return (
          <Form>
            <SyncAnswers
              onChange={onChange}
              onValidityChange={onValidityChange}
            />
            <div style={styles.list}>
              {/*{QUESTIONS.map((item) => (
                <div key={item.key} style={styles.card}>
                  <div style={styles.question}>{item.question}</div>
                  {show(item.key) && (
                    <div style={styles.error}>{errors.answers?.[item.key]}</div>
                  )}
                  <div style={styles.actions}>
                    <button
                      type="button"
                      onClick={() => setAnswer(item.key, true)}
                      style={{
                        ...styles.button,
                        ...(values.answers[item.key] === true
                          ? styles.buttonYesActive
                          : {}),
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnswer(item.key, false)}
                      style={{
                        ...styles.button,
                        ...(values.answers[item.key] === false
                          ? styles.buttonNoActive
                          : {}),
                      }}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}*/}

                    {QUESTIONS.map((item) => {
                        const answer = values.answers[item.key];
                        const blockingMsg =
                            item.key === "isIndianCitizen" && answer === false
                                ? NON_RESIDENT_MSG
                                : item.key === "isImmigrationVisa" && answer === true
                                    ? IMMIGRATION_VISA_MSG
                                    : item.key === "hasPreExistingCondition" && answer === true
                                        ? PRE_EXISTING_MSG
                                        : "";

                        return (
                            <div key={item.key} style={styles.card}>
                                <div style={styles.question}>{item.question}</div>

                                {show(item.key) && answer === null && (
                                    <div style={styles.error}>{errors.answers?.[item.key]}</div>
                                )}

                                <div style={styles.actions}>
                                    <button
                                        type="button"
                                        onClick={() => setAnswer(item.key, true)}
                                        style={{
                                            ...styles.button,
                                            ...(answer === true ? styles.buttonYesActive : {}),
                                        }}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAnswer(item.key, false)}
                                        style={{
                                            ...styles.button,
                                            ...(answer === false ? styles.buttonNoActive : {}),
                                        }}
                                    >
                                        No
                                    </button>
                                </div>

                                {blockingMsg && (
                                    <div style={{ ...styles.error, marginTop: 10, marginBottom: 0 }}>
                                        {blockingMsg}
                                    </div>
                                )}
                            </div>
                        );
                    })}

            </div>

            {isStudentTrip && (
              <div style={styles.sectionCard}>
                <div style={styles.sectionHead}>
                  <div style={styles.sectionTitle}>Student Details</div>
                  <div style={styles.sectionSub}>
                    Provide the student-specific details for this trip
                  </div>
                  <div className="mqf-section-line" />
                </div>

                <div style={styles.doctorGrid}>
                  <PfInput
                    label="University Name"
                    required
                    placeholder="Enter university name"
                    maxLength={120}
                    error={
                      showStudent("universityName")
                        ? errors.studentDetails?.universityName
                        : ""
                    }
                    value={values.studentDetails?.universityName || ""}
                    onChange={(e) =>
                      setStudentField("universityName", e.target.value)
                    }
                    onBlur={() =>
                      setFieldTouched("studentDetails.universityName", true)
                    }
                  />

                  <PfInput
                    label="Course Duration"
                    required
                    placeholder="Enter course duration"
                    maxLength={60}
                    error={
                      showStudent("courseDuration")
                        ? errors.studentDetails?.courseDuration
                        : ""
                    }
                    value={values.studentDetails?.courseDuration || ""}
                    onChange={(e) =>
                      setStudentField("courseDuration", e.target.value)
                    }
                    onBlur={() =>
                      setFieldTouched("studentDetails.courseDuration", true)
                    }
                  />

                  <PfInput
                    label="Number of Semester"
                    required
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter number of semester"
                    maxLength={3}
                    error={
                      showStudent("numberOfSemester")
                        ? errors.studentDetails?.numberOfSemester
                        : ""
                    }
                    value={values.studentDetails?.numberOfSemester || ""}
                    onChange={(e) =>
                      setStudentField(
                        "numberOfSemester",
                        e.target.value.replace(/\D/g, "").slice(0, 3),
                      )
                    }
                    onBlur={() =>
                      setFieldTouched("studentDetails.numberOfSemester", true)
                    }
                  />
                </div>
              </div>
            )}

            <div style={styles.sectionCard}>
              <div style={styles.sectionHead}>
                <div style={styles.sectionTitle}>Doctor Details</div>
                <div style={styles.sectionSub}>
                  If available provide the treating doctor's detail
                </div>
                <div className="mqf-section-line" />
              </div>

              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={Boolean(values.doctorDetails?.enabled)}
                  onChange={(e) => toggleDoctorDetails(e.target.checked)}
                  style={styles.checkbox}
                />
                <span style={styles.checkboxLabel}>Doctor Details</span>
              </label>

              {values.doctorDetails?.enabled && (
                <div style={styles.doctorGrid}>
                  <PfInput
                    label="Doctor Name"
                    required
                    placeholder="Enter first and last name"
                    maxLength={100}
                    error={
                      showDoctor("doctorName")
                        ? errors.doctorDetails?.doctorName
                        : ""
                    }
                    value={values.doctorDetails?.doctorName || ""}
                    onChange={(e) =>
                      setDoctorField("doctorName", e.target.value)
                    }
                    onBlur={() =>
                      setFieldTouched("doctorDetails.doctorName", true)
                    }
                  />

                  <PfInput
                    label="Address Line 1"
                    required
                    placeholder="Enter address line 1"
                    maxLength={100}
                    error={
                      showDoctor("addressLine1")
                        ? errors.doctorDetails?.addressLine1
                        : ""
                    }
                    value={values.doctorDetails?.addressLine1 || ""}
                    onChange={(e) =>
                      setDoctorField("addressLine1", e.target.value)
                    }
                    onBlur={() =>
                      setFieldTouched("doctorDetails.addressLine1", true)
                    }
                  />

                  <PfInput
                    label="Address Line 2"
                    required
                    placeholder="Enter address line 2"
                    maxLength={100}
                    error={
                      showDoctor("addressLine2")
                        ? errors.doctorDetails?.addressLine2
                        : ""
                    }
                    value={values.doctorDetails?.addressLine2 || ""}
                    onChange={(e) =>
                      setDoctorField("addressLine2", e.target.value)
                    }
                    onBlur={() =>
                      setFieldTouched("doctorDetails.addressLine2", true)
                    }
                  />

                  <PfInput
                    label="Mobile Number"
                    required
                    type="tel"
                    inputMode="numeric"
                    placeholder="10 digit mobile number"
                    maxLength={10}
                    error={
                      showDoctor("mobileNo")
                        ? errors.doctorDetails?.mobileNo
                        : ""
                    }
                    value={values.doctorDetails?.mobileNo || ""}
                    onChange={(e) =>
                      setDoctorField(
                        "mobileNo",
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    onBlur={() =>
                      setFieldTouched("doctorDetails.mobileNo", true)
                    }
                  />

                  {/* Pincode with city+state badge */}
                  <div style={{ position: "relative" }}>
                    <PfInput
                      label="Pincode"
                      required
                      type="tel"
                      inputMode="numeric"
                      placeholder="6 digit pincode"
                      maxLength={6}
                      error={
                        showDoctor("pincode")
                          ? errors.doctorDetails?.pincode
                          : ""
                      }
                      value={values.doctorDetails?.pincode || ""}
                      onChange={(e) => {
                        const val = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);
                        setDoctorField("pincode", val);
                        if (val.length === 6) {
                          setDoctorPincodeCity(null);
                          fetchPincodeCity(
                            val,
                            travellerDetails?.insurerID || "0",
                            (city) => {
                              setDoctorPincodeCity(city);
                              dispatch(
                                updateTravellerDetails({
                                  ...travellerDetails,
                                  doctorDetails: {
                                    ...values.doctorDetails,
                                    pincode: val,
                                    pincodeCity: city,
                                  },
                                }),
                              );
                            },
                            setDoctorPincodeLoading,
                          );
                        } else {
                          setDoctorPincodeCity(null);
                        }
                      }}
                      onBlur={() =>
                        setFieldTouched("doctorDetails.pincode", true)
                      }
                    />
                    {doctorPincodeLoading && (
                      <span style={styles.pincodeBadge}>Fetching...</span>
                    )}
                    {!doctorPincodeLoading && doctorPincodeCity?.CityName && (
                      <span style={styles.pincodeBadge}>
                        {doctorPincodeCity.CityName}
                        {/* {doctorPincodeCity.State_Name
                          ? `, ${doctorPincodeCity.state_Name}`
                          : ""} */}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {isStudentTrip && (
              <div style={styles.sectionCard}>
                <div style={styles.sectionHead}>
                  <div style={styles.sectionTitle}>Sponsor Details</div>
                  <div style={styles.sectionSub}>
                    If available provide the sponsor details for student trip
                  </div>
                  <div className="mqf-section-line" />
                </div>

                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={Boolean(values.sponsorDetails?.enabled)}
                    onChange={(e) => toggleSponsorDetails(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxLabel}>Sponsor Details</span>
                </label>

                {values.sponsorDetails?.enabled && (
                  <div style={styles.doctorGrid}>
                    <PfInput
                      label="Sponsor Name"
                      required
                      placeholder="Enter first and last name"
                      maxLength={100}
                      error={
                        showSponsor("sponsorName")
                          ? errors.sponsorDetails?.sponsorName
                          : ""
                      }
                      value={values.sponsorDetails?.sponsorName || ""}
                      onChange={(e) =>
                        setSponsorField("sponsorName", e.target.value)
                      }
                      onBlur={() =>
                        setFieldTouched("sponsorDetails.sponsorName", true)
                      }
                    />

                    <PfInput
                      label="Address Line 1"
                      required
                      placeholder="Enter address line 1"
                      maxLength={100}
                      error={
                        showSponsor("addressLine1")
                          ? errors.sponsorDetails?.addressLine1
                          : ""
                      }
                      value={values.sponsorDetails?.addressLine1 || ""}
                      onChange={(e) =>
                        setSponsorField("addressLine1", e.target.value)
                      }
                      onBlur={() =>
                        setFieldTouched("sponsorDetails.addressLine1", true)
                      }
                    />

                    <PfInput
                      label="Address Line 2"
                      required
                      placeholder="Enter address line 2"
                      maxLength={100}
                      error={
                        showSponsor("addressLine2")
                          ? errors.sponsorDetails?.addressLine2
                          : ""
                      }
                      value={values.sponsorDetails?.addressLine2 || ""}
                      onChange={(e) =>
                        setSponsorField("addressLine2", e.target.value)
                      }
                      onBlur={() =>
                        setFieldTouched("sponsorDetails.addressLine2", true)
                      }
                    />

                    <PfInput
                      label="Mobile Number"
                      required
                      type="tel"
                      inputMode="numeric"
                      placeholder="10 digit mobile number"
                      maxLength={10}
                      error={
                        showSponsor("mobileNo")
                          ? errors.sponsorDetails?.mobileNo
                          : ""
                      }
                      value={values.sponsorDetails?.mobileNo || ""}
                      onChange={(e) =>
                        setSponsorField(
                          "mobileNo",
                          e.target.value.replace(/\D/g, "").slice(0, 10),
                        )
                      }
                      onBlur={() =>
                        setFieldTouched("sponsorDetails.mobileNo", true)
                      }
                    />

                    <div style={{ position: "relative" }}>
                      <PfInput
                        label="Pincode"
                        required
                        type="tel"
                        inputMode="numeric"
                        placeholder="6 digit pincode"
                        maxLength={6}
                        error={
                          showSponsor("pincode")
                            ? errors.sponsorDetails?.pincode
                            : ""
                        }
                        value={values.sponsorDetails?.pincode || ""}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);
                          setSponsorField("pincode", val);
                          if (val.length === 6) {
                            setSponsorPincodeCity(null);
                            fetchPincodeCity(
                              val,
                              travellerDetails?.insurerID || "0",
                              (city) => {
                                setSponsorPincodeCity(city);
                                dispatch(
                                  updateTravellerDetails({
                                    ...travellerDetails,
                                    sponsorDetails: {
                                      ...values.sponsorDetails,
                                      pincode: val,
                                      pincodeCity: city,
                                    },
                                  }),
                                );
                              },
                              setSponsorPincodeLoading,
                            );
                          } else {
                            setSponsorPincodeCity(null);
                          }
                        }}
                        onBlur={() =>
                          setFieldTouched("sponsorDetails.pincode", true)
                        }
                      />
                      {sponsorPincodeLoading && (
                        <span style={styles.pincodeBadge}>Fetching...</span>
                      )}
                      {!sponsorPincodeLoading &&
                        sponsorPincodeCity?.CityName && (
                          <span style={styles.pincodeBadge}>
                            {sponsorPincodeCity.CityName}
                          </span>
                        )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isAnnualMultiTrip && (
              <div className="mqf-section-card">
                <div className="mqf-section-head">
                  <div className="mqf-section-title">Company Details</div>
                  <div className="mqf-section-sub">
                    If available provide the company details for annual multi
                    trip
                  </div>
                  <div className="mqf-section-line" />
                </div>

                <label className="mqf-checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(values.companyDetails?.enabled)}
                    onChange={(e) => toggleCompanyDetails(e.target.checked)}
                    className="mqf-checkbox"
                  />
                  <span className="mqf-checkbox-label">Company Details</span>
                </label>

                {values.companyDetails?.enabled && (
                  <div className="mqf-grid">
                    <PfInput
                      label="Company Name"
                      required
                      placeholder="Enter company name"
                      maxLength={100}
                      error={
                        showCompany("companyName")
                          ? errors.companyDetails?.companyName
                          : ""
                      }
                      value={values.companyDetails?.companyName || ""}
                      onChange={(e) =>
                        setCompanyField("companyName", e.target.value)
                      }
                      onBlur={() =>
                        setFieldTouched("companyDetails.companyName", true)
                      }
                    />

                    <PfInput
                      label="Address Line 1"
                      required
                      placeholder="Enter address line 1"
                      maxLength={100}
                      error={
                        showCompany("addressLine1")
                          ? errors.companyDetails?.addressLine1
                          : ""
                      }
                      value={values.companyDetails?.addressLine1 || ""}
                      onChange={(e) =>
                        setCompanyField("addressLine1", e.target.value)
                      }
                      onBlur={() =>
                        setFieldTouched("companyDetails.addressLine1", true)
                      }
                    />

                    <PfInput
                      label="Address Line 2"
                      required
                      placeholder="Enter address line 2"
                      maxLength={100}
                      error={
                        showCompany("addressLine2")
                          ? errors.companyDetails?.addressLine2
                          : ""
                      }
                      value={values.companyDetails?.addressLine2 || ""}
                      onChange={(e) =>
                        setCompanyField("addressLine2", e.target.value)
                      }
                      onBlur={() =>
                        setFieldTouched("companyDetails.addressLine2", true)
                      }
                    />

                    <PfInput
                      label="Mobile Number"
                      required
                      type="tel"
                      inputMode="numeric"
                      placeholder="10 digit mobile number"
                      maxLength={10}
                      error={
                        showCompany("mobileNo")
                          ? errors.companyDetails?.mobileNo
                          : ""
                      }
                      value={values.companyDetails?.mobileNo || ""}
                      onChange={(e) =>
                        setCompanyField(
                          "mobileNo",
                          e.target.value.replace(/\D/g, "").slice(0, 10),
                        )
                      }
                      onBlur={() =>
                        setFieldTouched("companyDetails.mobileNo", true)
                      }
                    />

                    {/* Pincode with city+state badge */}
                    <div style={{ position: "relative" }}>
                      <PfInput
                        label="Pincode"
                        required
                        type="tel"
                        inputMode="numeric"
                        placeholder="6 digit pincode"
                        maxLength={6}
                        error={
                          showCompany("pincode")
                            ? errors.companyDetails?.pincode
                            : ""
                        }
                        value={values.companyDetails?.pincode || ""}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6);
                          setCompanyField("pincode", val);
                          if (val.length === 6) {
                            setCompanyPincodeCity(null);
                            fetchPincodeCity(
                              val,
                              travellerDetails?.insurerID || "0",
                              (city) => {
                                setCompanyPincodeCity(city);
                                dispatch(
                                  updateTravellerDetails({
                                    ...travellerDetails,
                                    companyDetails: {
                                      ...values.companyDetails,
                                      pincode: val,
                                      pincodeCity: city,
                                    },
                                  }),
                                );
                              },
                              setCompanyPincodeLoading,
                            );
                          } else {
                            setCompanyPincodeCity(null);
                          }
                        }}
                        onBlur={() =>
                          setFieldTouched("companyDetails.pincode", true)
                        }
                      />
                      {companyPincodeLoading && (
                        <span style={styles.pincodeBadge}>Fetching...</span>
                      )}
                      {!companyPincodeLoading &&
                        companyPincodeCity?.CityName && (
                          <span style={styles.pincodeBadge}>
                            {companyPincodeCity.CityName}
                            {/* {companyPincodeCity.State_Name
                              ? `, ${companyPincodeCity.state_Name}`
                              : ""} */}
                          </span>
                        )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

const styles = {
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  sectionCard: {
    marginTop: 18,
    padding: 16,
    border: "1px solid rgba(27, 94, 60, 0.12)",
    borderRadius: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbf8 100%)",
    boxShadow: "0 6px 18px rgba(16, 38, 29, 0.04)",
  },
  sectionHead: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#10261d",
    marginBottom: 4,
  },
  sectionSub: {
    fontSize: 12,
    color: "#60786d",
    lineHeight: 1.5,
  },
  sectionLine: {
    marginTop: 12,
    borderTop: "1px solid rgba(27, 94, 60, 0.12)",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    userSelect: "none",
  },
  checkbox: {
    width: 16,
    height: 16,
    accentColor: "#1b5e3c",
    cursor: "pointer",
  },
  checkboxLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: "#10261d",
  },
  doctorGrid: {
    marginTop: "15px",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  card: {
    border: "1px solid rgba(27, 94, 60, 0.12)",
    borderRadius: 16,
    padding: 16,
    background: "#ffffff",
    boxShadow: "0 6px 18px rgba(16, 38, 29, 0.04)",
  },
  question: {
    fontSize: 14,
    fontWeight: 600,
    color: "#10261d",
    lineHeight: 1.5,
    marginBottom: 12,
  },
  error: {
    marginBottom: 10,
    fontSize: 12,
    color: "#dc3545",
    fontWeight: 600,
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  button: {
    minWidth: 86,
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid rgba(27, 94, 60, 0.14)",
    background: "#f7faf8",
    color: "#10261d",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    transition: "transform 120ms ease, background 120ms ease, color 120ms ease",
  },
  buttonYesActive: {
    background: "#1b5e3c",
    color: "#ffffff",
    borderColor: "#1b5e3c",
  },
  buttonNoActive: {
    background: "#7a1f2b",
    color: "#ffffff",
    borderColor: "#7a1f2b",
  },
  pincodeBadge: {
    position: "absolute",
    right: 10,
    top: "45%",
    transform: "translateY(-4px)", // nudge to sit in the input row, not the label
    fontSize: 11,
    fontWeight: 700,
    color: "#1b5e3c",
    background: "rgba(27,94,60,0.09)",
    borderRadius: 999,
    padding: "2px 8px",
    whiteSpace: "nowrap",
    maxWidth: 130,
    overflow: "hidden",
    textOverflow: "ellipsis",
    pointerEvents: "none",
    zIndex: 2,
  },
};

export default MemberQuestionsForm;
